import React, { useState, useEffect } from "react";
import {
  Calendar,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Save,
  X,
  Clock,
  Shield,
  FileText,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  mandatoService,
  Mandato,
  MandatoComNomes,
  StatusMandato,
} from "../services/mandatoService";
import { notifyError, notifySuccess } from "../utils/notify";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export const GestaoMandatos: React.FC = () => {
  const { user, empresaSelecionada } = useAuth();
  const [mandatos, setMandatos] = useState<MandatoComNomes[]>([]);
  const [loading, setLoading] = useState(true);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [mandatoEditando, setMandatoEditando] =
    useState<MandatoComNomes | null>(null);
  const [mostrarDialogExcluir, setMostrarDialogExcluir] = useState(false);
  const [mandatoParaExcluir, setMandatoParaExcluir] = useState<string | null>(
    null,
  );

  const [novoMandato, setNovoMandato] = useState<Partial<Mandato>>({
    numero_mandato: 1,
    data_inicio: "",
    data_fim: "",
    status: "ativo",
    eleicao_realizada: false,
    data_eleicao: "",
  });

  const carregarMandatos = async () => {
    if (!empresaSelecionada?.id) return;

    try {
      setLoading(true);
      const data = await mandatoService.listar(empresaSelecionada.id);
      setMandatos(data);
    } catch (error) {
      console.error("Erro ao carregar mandatos:", error);
      notifyError("Erro ao carregar mandatos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (empresaSelecionada?.id) {
      carregarMandatos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaSelecionada]);

  const abrirModalNovo = async () => {
    if (!empresaSelecionada?.id) return;

    const proximoNumero = await mandatoService.obterProximoNumero(
      empresaSelecionada.id,
    );

    setNovoMandato({
      numero_mandato: proximoNumero,
      data_inicio: "",
      data_fim: "",
      status: "ativo",
      eleicao_realizada: false,
      data_eleicao: "",
    });
    setMandatoEditando(null);
    setMostrarModal(true);
  };

  const abrirModalEditar = (mandato: MandatoComNomes) => {
    setNovoMandato({
      numero_mandato: mandato.numero_mandato,
      data_inicio: mandato.data_inicio,
      data_fim: mandato.data_fim,
      status: mandato.status,
      eleicao_realizada: mandato.eleicao_realizada,
      data_eleicao: mandato.data_eleicao || "",
      presidente_id: mandato.presidente_id || undefined,
      secretario_id: mandato.secretario_id || undefined,
    });
    setMandatoEditando(mandato);
    setMostrarModal(true);
  };

  const salvarMandato = async () => {
    if (!empresaSelecionada?.id || !user?.id) {
      notifyError("Empresa ou usuário não identificado");
      return;
    }

    if (!novoMandato.data_inicio || !novoMandato.data_fim) {
      notifyError("Datas de início e fim são obrigatórias");
      return;
    }

    // Validar duração do mandato (NR-05: 1 ou 2 anos)
    const dataInicio = new Date(novoMandato.data_inicio);
    const dataFim = new Date(novoMandato.data_fim);
    const diferencaAnos =
      (dataFim.getTime() - dataInicio.getTime()) /
      (1000 * 60 * 60 * 24 * 365.25);

    if (diferencaAnos < 0.95) {
      notifyError("Mandato deve ter no mínimo 1 ano (NR-05)");
      return;
    }

    if (diferencaAnos > 2.05) {
      notifyError(
        "Mandato não pode exceder 2 anos (NR-05 permite apenas 1 reeleição)",
      );
      return;
    }

    try {
      if (mandatoEditando?.id) {
        // Atualizar
        await mandatoService.atualizar(mandatoEditando.id, novoMandato);
        notifySuccess("Mandato atualizado com sucesso!");
      } else {
        // Criar novo
        const mandatoCompleto: Omit<
          Mandato,
          "id" | "created_at" | "updated_at"
        > = {
          empresa_id: empresaSelecionada.id,
          numero_mandato: novoMandato.numero_mandato || 1,
          data_inicio: novoMandato.data_inicio!,
          data_fim: novoMandato.data_fim!,
          status: (novoMandato.status as StatusMandato) || "ativo",
          eleicao_realizada: novoMandato.eleicao_realizada || false,
          data_eleicao: novoMandato.data_eleicao || null,
          presidente_id: novoMandato.presidente_id || null,
          secretario_id: novoMandato.secretario_id || null,
          criado_por: user.id,
        };

        await mandatoService.criar(mandatoCompleto);
        notifySuccess("Mandato criado com sucesso!");
      }

      setMostrarModal(false);
      carregarMandatos();
    } catch (error) {
      console.error("Erro ao salvar mandato:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Erro ao salvar mandato";
      notifyError(errorMessage);
    }
  };

  const confirmarExclusao = (id: string) => {
    setMandatoParaExcluir(id);
    setMostrarDialogExcluir(true);
  };

  const excluirMandato = async () => {
    if (!mandatoParaExcluir) return;

    try {
      await mandatoService.excluir(mandatoParaExcluir);
      notifySuccess("Mandato excluído com sucesso!");
      setMostrarDialogExcluir(false);
      setMandatoParaExcluir(null);
      carregarMandatos();
    } catch (error) {
      console.error("Erro ao excluir mandato:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Erro ao excluir mandato";
      notifyError(errorMessage);
    }
  };

  const finalizarMandato = async (id: string) => {
    try {
      await mandatoService.finalizar(id);
      notifySuccess("Mandato finalizado com sucesso!");
      carregarMandatos();
    } catch (error) {
      console.error("Erro ao finalizar mandato:", error);
      notifyError("Erro ao finalizar mandato");
    }
  };

  const getStatusBadge = (status: StatusMandato) => {
    const badges = {
      ativo: {
        color: "bg-green-100 text-green-800 border-green-200",
        icon: CheckCircle,
      },
      finalizado: {
        color: "bg-gray-100 text-gray-800 border-gray-200",
        icon: XCircle,
      },
      suspenso: {
        color: "bg-yellow-100 text-yellow-800 border-yellow-200",
        icon: AlertTriangle,
      },
    };

    const badge = badges[status];
    const Icon = badge.icon;

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${badge.color}`}
      >
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const calcularDuracaoMeses = (dataInicio: string, dataFim: string) => {
    return mandatoService.calcularDuracao(dataInicio, dataFim);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <Clock className="w-8 h-8 text-blue-600 animate-pulse mx-auto mb-2" />
          <p className="text-gray-600">Carregando mandatos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">
              Gestão de Mandatos da CIPA
            </h1>
            <p className="text-blue-100">{empresaSelecionada?.nome}</p>
            <p className="text-sm text-blue-200 mt-1">
              Conforme NR-05 - Mandatos de 1 a 2 anos
            </p>
          </div>
          <button
            onClick={abrirModalNovo}
            className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Novo Mandato
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-green-500">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-gray-700">
              Mandatos Ativos
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {mandatos.filter((m) => m.status === "ativo").length}
          </p>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-gray-500">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">
              Finalizados
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {mandatos.filter((m) => m.status === "finalizado").length}
          </p>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-blue-500">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-700">
              Total de Mandatos
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{mandatos.length}</p>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-yellow-500">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-yellow-600" />
            <span className="text-sm font-medium text-gray-700">
              Expirando (60 dias)
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {
              mandatos.filter(
                (m) =>
                  m.status === "ativo" &&
                  mandatoService.isExpirando(m.data_fim),
              ).length
            }
          </p>
        </div>
      </div>

      {/* Mandatos List */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          Histórico de Mandatos
        </h3>

        {mandatos.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 mb-2">Nenhum mandato cadastrado</p>
            <button
              onClick={abrirModalNovo}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              Criar primeiro mandato
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {mandatos.map((mandato) => {
              const duracao = calcularDuracaoMeses(
                mandato.data_inicio,
                mandato.data_fim,
              );
              const isExpirando =
                mandato.status === "ativo" &&
                mandatoService.isExpirando(mandato.data_fim);

              return (
                <div
                  key={mandato.id}
                  className={`border rounded-lg p-4 ${
                    isExpirando
                      ? "border-yellow-300 bg-yellow-50"
                      : "border-gray-200"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-semibold text-gray-900">
                          Mandato #{mandato.numero_mandato}
                        </h4>
                        {getStatusBadge(mandato.status)}
                        {isExpirando && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                            <AlertTriangle className="w-3 h-3" />
                            Expirando em breve
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-3">
                        <div>
                          <span className="font-medium">Período:</span>{" "}
                          {new Date(mandato.data_inicio).toLocaleDateString(
                            "pt-BR",
                          )}{" "}
                          -{" "}
                          {new Date(mandato.data_fim).toLocaleDateString(
                            "pt-BR",
                          )}
                        </div>
                        <div>
                          <span className="font-medium">Duração:</span>{" "}
                          {duracao} meses
                        </div>
                        <div>
                          <span className="font-medium">Eleição:</span>{" "}
                          {mandato.eleicao_realizada ? (
                            <span className="text-green-600">
                              ✓ Realizada
                              {mandato.data_eleicao &&
                                ` em ${new Date(mandato.data_eleicao).toLocaleDateString("pt-BR")}`}
                            </span>
                          ) : (
                            <span className="text-gray-500">Não realizada</span>
                          )}
                        </div>
                      </div>

                      {(mandato.presidente_nome || mandato.secretario_nome) && (
                        <div className="flex gap-4 text-sm text-gray-600 mb-2">
                          {mandato.presidente_nome && (
                            <div>
                              <span className="font-medium">Presidente:</span>{" "}
                              {mandato.presidente_nome}
                            </div>
                          )}
                          {mandato.secretario_nome && (
                            <div>
                              <span className="font-medium">Secretário:</span>{" "}
                              {mandato.secretario_nome}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => abrirModalEditar(mandato)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {mandato.status === "ativo" && (
                        <button
                          onClick={() => finalizarMandato(mandato.id!)}
                          className="p-2 text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
                          title="Finalizar"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => confirmarExclusao(mandato.id!)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal Criar/Editar */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {mandatoEditando ? "Editar Mandato" : "Novo Mandato"}
                </h3>
                <button
                  onClick={() => setMostrarModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Número do Mandato */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número do Mandato
                  </label>
                  <input
                    type="number"
                    value={novoMandato.numero_mandato || ""}
                    onChange={(e) =>
                      setNovoMandato({
                        ...novoMandato,
                        numero_mandato: parseInt(e.target.value),
                      })
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    min="1"
                  />
                </div>

                {/* Datas */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Início *
                    </label>
                    <input
                      type="date"
                      value={novoMandato.data_inicio || ""}
                      onChange={(e) =>
                        setNovoMandato({
                          ...novoMandato,
                          data_inicio: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Fim *
                    </label>
                    <input
                      type="date"
                      value={novoMandato.data_fim || ""}
                      onChange={(e) =>
                        setNovoMandato({
                          ...novoMandato,
                          data_fim: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={novoMandato.status || "ativo"}
                    onChange={(e) =>
                      setNovoMandato({
                        ...novoMandato,
                        status: e.target.value as StatusMandato,
                      })
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg"
                  >
                    <option value="ativo">Ativo</option>
                    <option value="finalizado">Finalizado</option>
                    <option value="suspenso">Suspenso</option>
                  </select>
                </div>

                {/* Eleição */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="eleicao_realizada"
                      checked={novoMandato.eleicao_realizada || false}
                      onChange={(e) =>
                        setNovoMandato({
                          ...novoMandato,
                          eleicao_realizada: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-blue-600"
                    />
                    <label
                      htmlFor="eleicao_realizada"
                      className="text-sm font-medium text-gray-700"
                    >
                      Eleição realizada
                    </label>
                  </div>

                  {novoMandato.eleicao_realizada && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Data da Eleição
                      </label>
                      <input
                        type="date"
                        value={novoMandato.data_eleicao || ""}
                        onChange={(e) =>
                          setNovoMandato({
                            ...novoMandato,
                            data_eleicao: e.target.value,
                          })
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setMostrarModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={salvarMandato}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  {mandatoEditando ? "Atualizar" : "Criar Mandato"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog
        open={mostrarDialogExcluir}
        onOpenChange={setMostrarDialogExcluir}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este mandato? Esta ação não pode
              ser desfeita.
              {mandatos.find((m) => m.id === mandatoParaExcluir)?.status ===
                "ativo" && (
                <span className="block mt-2 text-yellow-600 font-medium">
                  ⚠️ Atenção: Este é um mandato ativo!
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={excluirMandato}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
